"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useQuery } from "@urql/next";
import Fuse from "fuse.js";
import { graphql, type ResultOf } from "gql.tada";
import { useMemo } from "react";
import IwIcon from "@/assets/icons/iw.svg";
import SaIcon from "@/assets/icons/sa.svg";
import UaIcon from "@/assets/icons/ua.svg";
import { type AnalysisTarget } from "@/lib/ai/analysis-target";
import { cn } from "@/lib/utils";
import { useViewOrder } from "../../../_contexts/view-order-context";
import { reorderViewsByIds } from "../../sortable/sortable-utils";

/**
 * AI 候補 query は sidebar と同じく nested analytics relation の `id/__typename`
 * を必ず含める。
 *
 * これを省くと graphcache が `searchAnalytics` / `userAnalytics` / `bigQuery` を
 * 埋め込み partial object として扱い、初回ロード直後に AI パネルを開いたタイミングで
 * board/detail 側が保持している正規化済み relation を上書きしてしまうことがある。
 */
export const AnalysisTargetOptionsQuery = graphql(`
  query AnalysisTargetOptions($workspaceId: ID!) {
    node(id: $workspaceId) {
      __typename
      ... on Organization {
        projectOrderIW
        projectOrderSA
        projectOrderUA
        projects {
          id
          __typename
          name
          ... on IndexWorkerProject {
            color
            description
            indexWorkers {
              id
              name
            }
          }
          ... on SearchAnalyticsProject {
            id
            color
            description
            searchAnalytics {
              id
              __typename
              bigQuery {
                id
                __typename
                latestDataDate
              }
              views {
                id
                __typename
                name
                description
              }
            }
          }
          ... on UserAnalyticsProject {
            id
            color
            description
            userAnalytics {
              id
              __typename
              bigQuery {
                id
                __typename
                latestDataDate
              }
              views {
                id
                __typename
                name
                description
              }
            }
          }
        }
      }
    }
  }
`);

type AnalysisTargetOptionsQueryResult = ResultOf<
  typeof AnalysisTargetOptionsQuery
>;
type AnalysisTargetOptionsQueryNode = AnalysisTargetOptionsQueryResult["node"];
type AnalysisTargetOptionsQueryOrganization = Extract<
  AnalysisTargetOptionsQueryNode,
  { __typename: "Organization" }
>;
type AnalysisTargetOptionsProject =
  AnalysisTargetOptionsQueryOrganization["projects"][number];

/**
 * ピッカー/メンションで共通利用する分析対象候補の描画モデル。
 */
export type AnalysisTargetOption = {
  key: string;
  searchValue: string;
  label: string;
  description: string;
  projectTypeLabel: string;
  detail: string | null;
  target: AnalysisTarget;
  group: "project" | "view" | "index-worker";
  projectColor: string | null;
};

type AnalysisTargetProjectGroup = {
  project: AnalysisTargetOption;
  views: AnalysisTargetOption[];
};

export type AnalysisTargetTypeGroup = {
  projectType: AnalysisTarget["projectType"];
  shortLabel: "IW" | "SA" | "UA";
  label: string;
  projects: AnalysisTargetProjectGroup[];
};

/**
 * 分析対象候補の取得結果と UI 状態をひとまとめにした返却型。
 */
export type AnalysisTargetOptionsState = {
  options: AnalysisTargetOption[];
  groupedOptions: AnalysisTargetTypeGroup[];
  fetching: boolean;
  error: unknown;
};

type ProjectTypeLabels = {
  searchAnalytics: string;
  userAnalytics: string;
  indexWorker: string;
  searchAnalyticsView: string;
  userAnalyticsView: string;
  defaultView: string;
};

const PROJECT_TYPE_ORDER: Record<AnalysisTarget["projectType"], number> = {
  IndexWorkerProject: 0,
  SearchAnalyticsProject: 1,
  UserAnalyticsProject: 2,
};

const PROJECT_TYPE_SHORT_LABEL: Record<
  AnalysisTarget["projectType"],
  AnalysisTargetTypeGroup["shortLabel"]
> = {
  IndexWorkerProject: "IW",
  SearchAnalyticsProject: "SA",
  UserAnalyticsProject: "UA",
};

type AnalysisTargetBuildOptions = {
  /**
   * サイドバーと同じ project 並び（projectOrderIW/SA/UA）を適用する。
   */
  projectOrderByType?: Partial<
    Record<AnalysisTarget["projectType"], string[] | null | undefined>
  >;
  /**
   * 各 project 内の詳細（IW/ビュー）順を適用する。
   */
  viewOrderByProjectId?: Record<string, string[] | undefined>;
};

function normalizeName(name: string | null | undefined): string {
  return name?.trim() || "(no name)";
}

function normalizeDetail(detail: string | null | undefined): string | null {
  const normalized = detail?.trim() ?? "";
  return normalized.length > 0 ? normalized : null;
}

const SEARCH_NON_TEXT_PATTERN = /[\p{P}\p{S}]+/gu;
const SEARCH_WHITESPACE_PATTERN = /\s+/g;
const SEARCH_TOKEN_PATTERN = /[\p{L}\p{N}]+/gu;
const SEARCH_TOKEN_EXISTS_PATTERN = /[\p{L}\p{N}]/u;
const SEARCH_COMBINING_MARK_PATTERN = /\p{M}+/gu;
const KATAKANA_HIRAGANA_OFFSET = 0x60;
const KATAKANA_START = 0x30a1;
const KATAKANA_END = 0x30f6;

type AnalysisTargetQueryMatcher = {
  normalizedQuery: string;
  compactQuery: string;
  tokens: string[];
  locale: string;
};

type AnalysisTargetSearchDocument = {
  key: string;
  normalizedSearchValue: string;
  compactSearchValue: string;
};

const DEFAULT_SEARCH_LOCALE = "und";

/**
 * メンション検索用に入力文字列を正規化する。
 *
 * 契約:
 * - 全角/半角は NFKC で吸収する
 * - カタカナはひらがなへ寄せる（かな表記ゆれ吸収）
 * - 合成文字の濁点・アクセント記号を除去する
 * - 記号類は空白へ寄せ、トークン境界として扱う
 * - 出力は lower case + 連続空白圧縮済み
 */
function normalizeKanaToHiragana(value: string): string {
  return Array.from(value)
    .map((char) => {
      const codePoint = char.codePointAt(0);
      if (
        codePoint === undefined ||
        codePoint < KATAKANA_START ||
        codePoint > KATAKANA_END
      ) {
        return char;
      }
      return String.fromCodePoint(codePoint - KATAKANA_HIRAGANA_OFFSET);
    })
    .join("");
}

function normalizeSearchValue(value: string, locale: string): string {
  const normalized = value
    .normalize("NFKC")
    .toLocaleLowerCase(locale)
    .normalize("NFKD")
    .replace(SEARCH_COMBINING_MARK_PATTERN, "");
  return normalizeKanaToHiragana(normalized)
    .replace(SEARCH_NON_TEXT_PATTERN, " ")
    .replace(SEARCH_WHITESPACE_PATTERN, " ")
    .trim();
}

/**
 * 文字が飛び飛びでも順序が保たれていれば一致とみなす。
 *
 * 例: `jade` は `j-a_d e` といった疎な入力にも一致する。
 */
function isSubsequenceMatch(token: string, source: string): boolean {
  if (token.length === 0) {
    return true;
  }
  let tokenIndex = 0;
  for (const char of source) {
    if (char === token[tokenIndex]) {
      tokenIndex += 1;
      if (tokenIndex >= token.length) {
        return true;
      }
    }
  }
  return false;
}

function splitByCharacterCategory(token: string): string[] {
  const segments: string[] = [];
  let current = "";
  let currentCategory: "latin" | "digit" | "word" | null = null;

  Array.from(token).forEach((char) => {
    const category = /[a-z]/.test(char)
      ? "latin"
      : /[0-9]/.test(char)
        ? "digit"
        : "word";
    if (currentCategory === category) {
      current += char;
      return;
    }
    if (current.length > 0) {
      segments.push(current);
    }
    current = char;
    currentCategory = category;
  });

  if (current.length > 0) {
    segments.push(current);
  }

  return segments;
}

/**
 * 入力クエリを言語依存で単語分割し、英字/数字/その他語句の境界へ再分割する。
 *
 * 例:
 * - `jade2022` -> `jade`, `2022`
 * - `jad年` -> `jad`, `年`
 * - `ja以降` -> `ja`, `以降`
 */
function splitSearchTokens(normalizedQuery: string, locale: string): string[] {
  const segmentedTokens =
    typeof Intl.Segmenter === "function"
      ? Array.from(
          new Intl.Segmenter(locale, { granularity: "word" }).segment(
            normalizedQuery,
          ),
        )
          .map((segment) => segment.segment)
          .filter((segment) => SEARCH_TOKEN_EXISTS_PATTERN.test(segment))
          .flatMap((segment) => splitByCharacterCategory(segment))
      : [];
  if (segmentedTokens.length > 0) {
    return segmentedTokens.map((token) => token.trim()).filter(Boolean);
  }

  return (normalizedQuery.match(SEARCH_TOKEN_PATTERN) ?? [])
    .flatMap((token) => splitByCharacterCategory(token))
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

function buildAnalysisTargetQueryMatcher(
  query: string,
  locale?: string,
): AnalysisTargetQueryMatcher {
  const normalizedLocale = locale || DEFAULT_SEARCH_LOCALE;
  const normalizedQuery = normalizeSearchValue(query, normalizedLocale);
  return {
    normalizedQuery,
    compactQuery: normalizedQuery.replace(SEARCH_WHITESPACE_PATTERN, ""),
    tokens: splitSearchTokens(normalizedQuery, normalizedLocale),
    locale: normalizedLocale,
  };
}

/**
 * `Fuse` で曖昧一致の候補を取りつつ、includes/subsequence も併用して
 * 短いクエリ・言語混在クエリの取りこぼしを防ぐ。
 */
function searchDocumentKeysByTerm(
  documents: AnalysisTargetSearchDocument[],
  fuse: Fuse<AnalysisTargetSearchDocument>,
  term: string,
): Set<string> {
  if (term.length === 0) {
    return new Set(documents.map((document) => document.key));
  }
  const strictResult = new Set<string>();
  documents.forEach((document) => {
    if (
      document.normalizedSearchValue.includes(term) ||
      document.compactSearchValue.includes(term) ||
      isSubsequenceMatch(term, document.compactSearchValue)
    ) {
      strictResult.add(document.key);
    }
  });
  if (strictResult.size > 0) {
    return strictResult;
  }

  return new Set(fuse.search(term).map(({ item }) => item.key));
}

/**
 * 候補の `searchValue` が検索クエリに一致するか判定する。
 *
 * 判定順:
 * 1. 正規化済み全文一致（`includes`）
 * 2. 空白除去後の連結一致（`jade 2022` / `jade2022` 双方向吸収）
 * 3. トークン AND 一致（親名 + 子名に跨る曖昧入力を吸収）
 * 4. 上記に `Fuse` の曖昧一致結果をマージし、多言語の表記ゆれを補完
 */
function getMatchedSearchValueSet(
  searchValues: string[],
  matcher: AnalysisTargetQueryMatcher,
): Set<string> {
  if (!matcher.normalizedQuery) {
    return new Set(searchValues);
  }
  const documents = searchValues.map((searchValue) => {
    const normalizedSearchValue = normalizeSearchValue(
      searchValue,
      matcher.locale,
    );
    return {
      key: searchValue,
      normalizedSearchValue,
      compactSearchValue: normalizedSearchValue.replace(
        SEARCH_WHITESPACE_PATTERN,
        "",
      ),
    } satisfies AnalysisTargetSearchDocument;
  });
  const fuse = new Fuse(documents, {
    includeScore: true,
    shouldSort: false,
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 1,
    keys: [
      { name: "normalizedSearchValue", weight: 0.7 },
      { name: "compactSearchValue", weight: 0.3 },
    ],
  });

  const matchedByQuery = searchDocumentKeysByTerm(
    documents,
    fuse,
    matcher.normalizedQuery,
  );
  const matchedByCompact =
    matcher.compactQuery.length > 0
      ? searchDocumentKeysByTerm(documents, fuse, matcher.compactQuery)
      : new Set<string>();
  const matchedByTokens = matcher.tokens.reduce<Set<string> | null>(
    (previous, token) => {
      const current = searchDocumentKeysByTerm(documents, fuse, token);
      if (previous === null) {
        return current;
      }
      return new Set(
        Array.from(previous).filter((searchValue) => current.has(searchValue)),
      );
    },
    null,
  );

  const merged = new Set<string>([...matchedByQuery, ...matchedByCompact]);
  if (matchedByTokens) {
    matchedByTokens.forEach((searchValue) => {
      merged.add(searchValue);
    });
  }
  return merged;
}

function reorderByIds<T>(
  items: T[],
  orderIds: string[] | null | undefined,
  getId: (item: T) => string,
): T[] {
  if (!orderIds || orderIds.length === 0) {
    return items;
  }

  const itemMap = new Map<string, T>();
  items.forEach((item) => {
    itemMap.set(getId(item), item);
  });

  const orderedItems: T[] = [];
  orderIds.forEach((id) => {
    const matched = itemMap.get(id);
    if (!matched) {
      return;
    }
    orderedItems.push(matched);
    itemMap.delete(id);
  });

  orderedItems.push(...itemMap.values());
  return orderedItems;
}

function getProjectTypeLabel(
  projectType: AnalysisTarget["projectType"],
  labels: ProjectTypeLabels,
): string {
  if (projectType === "SearchAnalyticsProject") {
    return labels.searchAnalytics;
  }
  if (projectType === "UserAnalyticsProject") {
    return labels.userAnalytics;
  }
  return labels.indexWorker;
}

type SupportedAnalysisTargetProject = Exclude<
  AnalysisTargetOptionsProject,
  { __typename: "AdsAnalyticsProject" }
>;

function toBaseTarget(project: SupportedAnalysisTargetProject): AnalysisTarget {
  return {
    projectId: project.id,
    projectName: normalizeName(project.name),
    projectType: project.__typename,
  };
}

export function buildAnalysisTargetOptionGroups(
  projects: AnalysisTargetOptionsProject[],
  labels: ProjectTypeLabels,
  buildOptions: AnalysisTargetBuildOptions = {},
): AnalysisTargetTypeGroup[] {
  const groupedProjects = new Map<
    AnalysisTarget["projectType"],
    AnalysisTargetProjectGroup[]
  >();

  projects.forEach((project) => {
    // AdsAnalyticsProject は AI チャットの分析対象に未対応のためスキップする
    if (project.__typename === "AdsAnalyticsProject") return;

    const baseTarget = toBaseTarget(project as SupportedAnalysisTargetProject);
    const projectTypeLabel = getProjectTypeLabel(
      baseTarget.projectType,
      labels,
    );
    const projectDetail = normalizeDetail(
      (project as SupportedAnalysisTargetProject).description,
    );
    const projectOption: AnalysisTargetOption = {
      key: `project-${project.id}`,
      searchValue: baseTarget.projectName.toLowerCase(),
      label: baseTarget.projectName,
      description: projectTypeLabel,
      projectTypeLabel,
      detail: projectDetail,
      target: baseTarget,
      group: "project",
      projectColor: "color" in project ? (project.color ?? null) : null,
    };

    let viewOptions: AnalysisTargetOption[] = [];
    const orderedViewIds = buildOptions.viewOrderByProjectId?.[project.id];

    if (project.__typename === "SearchAnalyticsProject") {
      const views = reorderViewsByIds(
        project.searchAnalytics?.views ?? [],
        orderedViewIds,
      );
      const detailViewOptions = views.map((view) => {
        const viewName = normalizeName(view.name);
        const target: AnalysisTarget = {
          ...baseTarget,
          viewId: view.id,
          viewName,
        };
        return {
          key: `view-${view.id}`,
          searchValue: `${baseTarget.projectName} ${viewName}`.toLowerCase(),
          label: viewName,
          description: labels.searchAnalyticsView,
          projectTypeLabel,
          detail: normalizeDetail(view.description) ?? projectDetail,
          target,
          group: "view",
          projectColor: project.color ?? null,
        } satisfies AnalysisTargetOption;
      });

      const allViewOption: AnalysisTargetOption[] = project.searchAnalytics
        ?.bigQuery?.latestDataDate
        ? [
            {
              key: `all-${project.id}`,
              searchValue:
                `${baseTarget.projectName} ${labels.defaultView} all 全体 default`.toLowerCase(),
              label: labels.defaultView,
              description: labels.searchAnalyticsView,
              projectTypeLabel,
              detail: projectDetail,
              target: baseTarget,
              group: "view",
              projectColor: project.color ?? null,
            },
          ]
        : [];

      // サイドバー同様に「全体」を先頭へ固定し、以降は並び順を保持する。
      viewOptions = [...allViewOption, ...detailViewOptions];
    } else if (project.__typename === "UserAnalyticsProject") {
      const views = reorderViewsByIds(
        project.userAnalytics?.views ?? [],
        orderedViewIds,
      );
      const detailViewOptions = views.map((view) => {
        const viewName = normalizeName(view.name);
        const target: AnalysisTarget = {
          ...baseTarget,
          viewId: view.id,
          viewName,
        };
        return {
          key: `view-${view.id}`,
          searchValue: `${baseTarget.projectName} ${viewName}`.toLowerCase(),
          label: viewName,
          description: labels.userAnalyticsView,
          projectTypeLabel,
          detail: normalizeDetail(view.description) ?? projectDetail,
          target,
          group: "view",
          projectColor: project.color ?? null,
        } satisfies AnalysisTargetOption;
      });

      const allViewOption: AnalysisTargetOption[] = project.userAnalytics
        ?.bigQuery?.latestDataDate
        ? [
            {
              key: `all-${project.id}`,
              searchValue:
                `${baseTarget.projectName} ${labels.defaultView} all 全体 default`.toLowerCase(),
              label: labels.defaultView,
              description: labels.userAnalyticsView,
              projectTypeLabel,
              detail: projectDetail,
              target: baseTarget,
              group: "view",
              projectColor: project.color ?? null,
            },
          ]
        : [];

      // サイドバー同様に「全体」を先頭へ固定し、以降は並び順を保持する。
      viewOptions = [...allViewOption, ...detailViewOptions];
    } else if (project.__typename === "IndexWorkerProject") {
      const indexWorkers = reorderViewsByIds(
        project.indexWorkers ?? [],
        orderedViewIds,
      );
      viewOptions = indexWorkers.map((indexWorker) => {
        const iwName = normalizeName(indexWorker.name);
        const target: AnalysisTarget = {
          ...baseTarget,
          iwId: indexWorker.id,
          iwName,
        };
        return {
          key: `iw-${indexWorker.id}`,
          searchValue: `${baseTarget.projectName} ${iwName}`.toLowerCase(),
          label: iwName,
          description: labels.indexWorker,
          projectTypeLabel,
          detail: projectDetail,
          target,
          group: "index-worker",
          projectColor: project.color ?? null,
        } satisfies AnalysisTargetOption;
      });
    }

    const nextProjectGroup: AnalysisTargetProjectGroup = {
      project: projectOption,
      views: viewOptions,
    };
    const current = groupedProjects.get(baseTarget.projectType);
    if (!current) {
      groupedProjects.set(baseTarget.projectType, [nextProjectGroup]);
      return;
    }
    current.push(nextProjectGroup);
  });

  return Object.entries(PROJECT_TYPE_ORDER)
    .sort((a, b) => a[1] - b[1])
    .flatMap(([projectType]) => {
      const typedProjectType = projectType as AnalysisTarget["projectType"];
      const projectsInType = groupedProjects.get(typedProjectType);
      if (!projectsInType || projectsInType.length === 0) {
        return [];
      }
      const orderedProjects = reorderByIds(
        projectsInType,
        buildOptions.projectOrderByType?.[typedProjectType],
        (projectGroup) => projectGroup.project.target.projectId,
      );
      return [
        {
          projectType: typedProjectType,
          shortLabel: PROJECT_TYPE_SHORT_LABEL[typedProjectType],
          label: getProjectTypeLabel(typedProjectType, labels),
          projects: orderedProjects,
        } satisfies AnalysisTargetTypeGroup,
      ];
    });
}

export function flattenAnalysisTargetOptionGroups(
  groupedOptions: AnalysisTargetTypeGroup[],
): AnalysisTargetOption[] {
  return groupedOptions.flatMap((typeGroup) =>
    typeGroup.projects.flatMap((projectGroup) => [
      projectGroup.project,
      ...projectGroup.views,
    ]),
  );
}

/**
 * ワークスペース配下の project/view 候補を取得し、UI がそのまま描画できる形へ正規化する。
 *
 * 入力: `workspaceId`（空文字時は query を pause）
 * 出力: project/view の統合候補、グループ別候補、取得状態
 * 契約:
 * - 候補の並び順は projectType の優先順（IW -> SA -> UA）を維持する。
 * - project/view/IW はサイドバーの並び順（projectOrder + viewOrder）に追従する。
 * - SA/UA は最新データがある project のみ「全体」を先頭候補に含める。
 * 失敗時: GraphQL エラーは握り潰さず `error` として呼び出し元へ返す
 */
export function useAnalysisTargetOptions(
  workspaceId: string,
  shouldFetchOptions = true,
): AnalysisTargetOptionsState {
  const { i18n } = useLingui();
  const { getViewOrder } = useViewOrder();
  /**
   * 現在ページの board/detail query が current project の partial shape を cache へ
   * 先に書くことがあるため、候補一覧は常に network で完全な project/view 一覧を再確認する。
   *
   * これを cache-first のままにすると、現在ページの project だけ `views` が欠けた
   * 状態で `@` メニューへ出ることがあり、配下 view が見えなくなる。
   */
  const [{ data, fetching, error }] = useQuery({
    query: AnalysisTargetOptionsQuery,
    variables: { workspaceId },
    pause: !shouldFetchOptions || workspaceId.length === 0,
    requestPolicy: "cache-and-network",
  });

  const groupedOptions = useMemo(() => {
    const organization = data?.node;
    if (organization?.__typename !== "Organization") {
      return [];
    }

    const labels: ProjectTypeLabels = {
      searchAnalytics: i18n._(msg`Search Analytics`),
      userAnalytics: i18n._(msg`User Analytics`),
      indexWorker: i18n._(msg`Index Worker`),
      searchAnalyticsView: i18n._(msg`Search Analytics view`),
      userAnalyticsView: i18n._(msg`User Analytics view`),
      defaultView: i18n._(
        msg({
          id: "default-view.name",
          message: "All",
        }),
      ),
    };

    const projectOrderByType: AnalysisTargetBuildOptions["projectOrderByType"] =
      {
        IndexWorkerProject: organization.projectOrderIW,
        SearchAnalyticsProject: organization.projectOrderSA,
        UserAnalyticsProject: organization.projectOrderUA,
      };

    const viewOrderByProjectId = organization.projects.reduce<
      Record<string, string[] | undefined>
    >((map, project) => {
      const projectType = project.__typename;
      const fallbackOrder =
        projectType === "IndexWorkerProject"
          ? organization.projectOrderIW
          : projectType === "SearchAnalyticsProject"
            ? organization.projectOrderSA
            : organization.projectOrderUA;
      map[project.id] = getViewOrder(project.id) ?? fallbackOrder ?? undefined;
      return map;
    }, {});

    return buildAnalysisTargetOptionGroups(organization.projects, labels, {
      projectOrderByType,
      viewOrderByProjectId,
    });
  }, [data, getViewOrder, i18n]);

  const options = flattenAnalysisTargetOptionGroups(groupedOptions);

  return {
    options,
    groupedOptions,
    fetching,
    error,
  };
}

/**
 * 階層候補一覧をメンション/ピッカー検索用に部分一致フィルタする。
 *
 * 契約:
 * - プロジェクト一致時の配下詳細（view / index worker）表示は options で切り替える
 * - 詳細のみ一致時は親プロジェクトを残し、一致詳細のみ返す
 */
export function filterAnalysisTargetOptionGroups(
  groupedOptions: AnalysisTargetTypeGroup[],
  query: string,
  options?: {
    includeChildOptionsWhenProjectMatched?: boolean;
    locale?: string;
  },
): AnalysisTargetTypeGroup[] {
  const queryMatcher = buildAnalysisTargetQueryMatcher(query, options?.locale);
  if (!queryMatcher.normalizedQuery) {
    return groupedOptions;
  }
  const includeChildOptionsWhenProjectMatched =
    options?.includeChildOptionsWhenProjectMatched ?? true;
  const searchValues = flattenAnalysisTargetOptionGroups(groupedOptions).map(
    (option) => option.searchValue,
  );
  const matchedSearchValueSet = getMatchedSearchValueSet(
    searchValues,
    queryMatcher,
  );

  return groupedOptions
    .map((typeGroup) => {
      const filteredProjects = typeGroup.projects.flatMap((projectGroup) => {
        const projectMatched = matchedSearchValueSet.has(
          projectGroup.project.searchValue,
        );
        if (projectMatched) {
          return [
            includeChildOptionsWhenProjectMatched
              ? projectGroup
              : { ...projectGroup, views: [] },
          ];
        }
        const filteredViews = projectGroup.views.filter((viewOption) =>
          matchedSearchValueSet.has(viewOption.searchValue),
        );
        if (filteredViews.length === 0) {
          return [];
        }
        return [{ ...projectGroup, views: filteredViews }];
      });
      if (filteredProjects.length === 0) {
        return [];
      }
      return [{ ...typeGroup, projects: filteredProjects }];
    })
    .flat();
}

/**
 * 既に選択済みの target を候補グループから除外する。
 *
 * 同一コンテキストの重複選択を UI 上で防ぐため、project/view のキー一致で除外する。
 */
export function excludeSelectedAnalysisTargetOptionGroups(
  groupedOptions: AnalysisTargetTypeGroup[],
  selectedTargets: AnalysisTarget[],
): AnalysisTargetTypeGroup[] {
  if (selectedTargets.length === 0) {
    return groupedOptions;
  }
  const selectedKeys = new Set(
    selectedTargets.map((target) => getAnalysisTargetOptionKey(target)),
  );
  const isOptionSelected = (option: AnalysisTargetOption): boolean =>
    selectedKeys.has(option.key) ||
    selectedKeys.has(getAnalysisTargetOptionKey(option.target));

  return groupedOptions
    .map((typeGroup) => {
      const projects = typeGroup.projects
        .map((projectGroup) => {
          const projectSelected = isOptionSelected(projectGroup.project);
          const views = projectGroup.views.filter(
            (viewOption) => !isOptionSelected(viewOption),
          );
          if (projectSelected && views.length === 0) {
            return null;
          }
          return {
            ...projectGroup,
            project: projectGroup.project,
            views,
          };
        })
        .filter((group): group is AnalysisTargetProjectGroup => Boolean(group));

      if (projects.length === 0) {
        return null;
      }
      return {
        ...typeGroup,
        projects,
      };
    })
    .filter((group): group is AnalysisTargetTypeGroup => Boolean(group));
}

/**
 * `AnalysisTarget` を候補キー形式（`project-xxx` / `view-xxx`）へ変換する。
 */
export function getAnalysisTargetOptionKey(target: AnalysisTarget): string {
  if (target.iwId) {
    return `iw-${target.iwId}`;
  }
  if (target.viewId) {
    return `view-${target.viewId}`;
  }
  return `project-${target.projectId}`;
}

type AnalysisTargetProjectColorBadgeProps = {
  color: string | null;
  className?: string;
};

type AnalysisTargetTypeIconProps = {
  projectType: AnalysisTarget["projectType"];
  className?: string;
};

/**
 * プロジェクト色を表す小型バッジ。
 */
export function AnalysisTargetProjectColorBadge({
  color,
  className,
}: AnalysisTargetProjectColorBadgeProps) {
  return (
    <span
      className={cn(
        "relative size-4 shrink-0 rounded-sm border border-border/60",
        className,
      )}
    >
      <span
        className={cn(
          "absolute inset-[2px] rounded-[2px] bg-muted-foreground/30",
          color === "#000000" && "bg-black dark:bg-white",
        )}
        style={{
          backgroundColor: color && color !== "#000000" ? color : undefined,
        }}
      />
    </span>
  );
}

/**
 * project 種別に応じた識別アイコン（IW/SA/UA）を返す。
 */
export function AnalysisTargetTypeIcon({
  projectType,
  className,
}: AnalysisTargetTypeIconProps) {
  const iconClassName = cn("size-4 shrink-0", className);
  if (projectType === "IndexWorkerProject") {
    return <IwIcon className={iconClassName} />;
  }
  if (projectType === "SearchAnalyticsProject") {
    return <SaIcon className={iconClassName} />;
  }
  return <UaIcon className={iconClassName} />;
}
